import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import book1Pages from "../data/book1";
import book2Pages from "../data/book2";

export default function PoetryBookPage() {
  const navigate = useNavigate();
  const { bookId } = useParams();
  const [currentPage, setCurrentPage] = useState(0);
  const [isFlipping, setIsFlipping] = useState(false);
  const [showIndex, setShowIndex] = useState(false);

  const bookData = useMemo(() => {
    if (bookId === "book-1") {
      return {
        title: "NOTE 1",
        pages: book1Pages,
      };
    }

    if (bookId === "book-2") {
      return {
        title: "NOTE 2",
        pages: book2Pages,
      };
    }

    return {
      title: "UNKNOWN NOTE",
      pages: [],
    };
  }, [bookId]);

  useEffect(() => {
    setCurrentPage(0);
    setShowIndex(false);
  }, [bookId]);

  const totalPages = bookData.pages.length;
  const page = bookData.pages[currentPage];

  const handlePageChange = (nextIndex) => {
    if (nextIndex < 0 || nextIndex >= totalPages || isFlipping) return;

    setIsFlipping(true);

    setTimeout(() => {
      setCurrentPage(nextIndex);
      setTimeout(() => {
        setIsFlipping(false);
      }, 140);
    }, 140);
  };

  const handlePrev = () => {
    handlePageChange(currentPage - 1);
  };

  const handleNext = () => {
    handlePageChange(currentPage + 1);
  };

  const handleJumpToPage = (index) => {
    if (index === currentPage) {
      setShowIndex(false);
      return;
    }

    handlePageChange(index);
    setShowIndex(false);
  };

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "ArrowLeft") handlePrev();
      if (e.key === "ArrowRight") handleNext();
      if (e.key.toLowerCase() === "i") setShowIndex((prev) => !prev);
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  });

  if (!page) {
    return (
      <div className="min-h-screen bg-brandBlack text-brandWhite p-8 flex flex-col items-center justify-center text-center">
        <h1 className="text-3xl font-bold mb-4">NO PAGES FOUND</h1>
        <button
          onClick={() => navigate("/nep2yn")}
          className="border border-gray-700 px-6 py-3 hover:border-white transition"
        >
          Back
        </button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-brandBlack text-brandWhite px-4 py-8 md:px-8">
      <div className="max-w-5xl mx-auto">
        <div className="flex items-center justify-between mb-8 gap-4">
          <button
            onClick={() => navigate("/nep2yn")}
            className="text-sm text-gray-400 underline"
          >
            ← Back
          </button>

          <div className="text-center">
            <h1 className="text-2xl md:text-3xl font-bold tracking-[0.2em]">
              {bookData.title}
            </h1>
          </div>

          <button
            onClick={() => setShowIndex((prev) => !prev)}
            className="text-sm text-gray-400 underline min-w-[70px] text-right"
          >
            {showIndex ? "Close Index" : "Page Index"}
          </button>
        </div>

        {showIndex && (
          <div className="max-w-3xl mx-auto mb-6 border border-gray-700 rounded-2xl bg-[#0a0a0a] p-5 md:p-6">
            <div className="text-center mb-5">
              <h2 className="text-lg md:text-xl tracking-[0.25em] text-white">
                PAGE INDEX
              </h2>
            </div>

            <div className="flex flex-wrap gap-3 justify-center">
              {bookData.pages.map((entry, index) => (
                <button
                  key={index}
                  onClick={() => handleJumpToPage(index)}
                  className={`px-4 py-2 rounded-lg border text-sm tracking-[0.18em] transition ${
                    currentPage === index
                      ? "border-white text-white bg-[#141414]"
                      : "border-gray-700 text-gray-400 hover:border-white hover:text-white hover:bg-[#111111]"
                  }`}
                >
                  {entry.pageNumber || String(index + 1).padStart(3, "0")}
                </button>
              ))}
            </div>
          </div>
        )}

        <div className="flex items-center justify-center">
          <div
            className={`w-full max-w-3xl min-h-[68vh] rounded-2xl border border-gray-700 bg-[#0d0d0d] shadow-[0_0_30px_rgba(255,255,255,0.04)] px-6 py-8 md:px-12 md:py-12 transition-all duration-200 ${
              isFlipping ? "opacity-0 scale-[0.985]" : "opacity-100 scale-100"
            }`}
          >
            <div className="flex flex-col h-full justify-between">
              <div>
                <div className="text-center mb-8">
                  <div className="text-xs md:text-sm text-gray-500 tracking-[0.3em] mb-3">
                    PAGE {page.pageNumber || String(currentPage + 1).padStart(3, "0")}
                  </div>

                  {page.heading && (
                    <h2 className="text-2xl md:text-3xl font-semibold uppercase tracking-[0.18em]">
                      {page.heading}
                    </h2>
                  )}

                  {page.subtitle && (
                    <p className="mt-3 text-sm md:text-base text-gray-400 italic tracking-[0.12em]">
                      {page.subtitle}
                    </p>
                  )}
                </div>

                <div className="whitespace-pre-wrap text-white text-sm md:text-lg leading-8 text-center max-w-2xl mx-auto">
                  {page.body || ""}
                </div>
              </div>

              <div className="mt-12 pt-6 border-t border-gray-700 flex items-center justify-between">
                <button
                  onClick={handlePrev}
                  disabled={currentPage === 0}
                  className={`px-4 py-3 border rounded-xl text-sm tracking-[0.15em] transition ${
                    currentPage === 0
                      ? "border-gray-800 text-gray-700 cursor-not-allowed"
                      : "border-gray-600 text-white hover:border-white hover:bg-[#151515]"
                  }`}
                >
                  ← PREVIOUS
                </button>

                <div className="text-xs md:text-sm text-gray-500 tracking-[0.2em]">
                  PAGE {page.pageNumber || String(currentPage + 1).padStart(3, "0")}
                </div>

                <button
                  onClick={handleNext}
                  disabled={currentPage === totalPages - 1}
                  className={`px-4 py-3 border rounded-xl text-sm tracking-[0.15em] transition ${
                    currentPage === totalPages - 1
                      ? "border-gray-800 text-gray-700 cursor-not-allowed"
                      : "border-gray-600 text-white hover:border-white hover:bg-[#151515]"
                  }`}
                >
                  NEXT →
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}